import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown } from 'lucide-react';
import CrawlCard from '@/components/CrawlCard';
import MetaTags from '@/components/MetaTags';
import axios from 'axios';
import { API } from '@/App';

const cityToState = {
  'Miami': 'Florida',
  'Austin': 'Texas',
  'New York': 'New York',
  'Nashville': 'Tennessee',
  'Chicago': 'Illinois',
  'San Diego': 'California',
  'Denver': 'Colorado',
  'Los Angeles': 'California',
  'Las Vegas': 'Nevada',
  'New Orleans': 'Louisiana',
  'Atlanta': 'Georgia',
  'Dallas': 'Texas',
  'Houston': 'Texas',
  'Phoenix': 'Arizona',
  'Seattle': 'Washington',
  'Portland': 'Oregon',
  'Boston': 'Massachusetts',
  'Philadelphia': 'Pennsylvania',
  'Detroit': 'Michigan',
  'Minneapolis': 'Minnesota',
};

const CrawlsPage = () => {
  const [crawls, setCrawls] = useState([]);
  const [filteredCrawls, setFilteredCrawls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');

  useEffect(() => {
    fetchCrawls();
  }, []);

  useEffect(() => {
    filterCrawls();
  }, [crawls, searchCity, activeFilter, stateFilter]);

  const fetchCrawls = async () => {
    try {
      const response = await axios.get(`${API}/crawls`);
      setCrawls(response.data);
      setFilteredCrawls(response.data);
    } catch (error) {
      console.error('Error fetching crawls:', error);
    }
    setLoading(false);
  };

  const filterCrawls = () => {
    let filtered = [...crawls];

    // Filter out test entries
    filtered = filtered.filter(
      (crawl) => !crawl.city?.toLowerCase().includes('test') && !crawl.title?.toLowerCase().includes('test')
    );

    if (searchCity) {
      filtered = filtered.filter(
        (crawl) =>
          crawl.city.toLowerCase().includes(searchCity.toLowerCase()) ||
          crawl.neighborhood.toLowerCase().includes(searchCity.toLowerCase())
      );
    }

    if (stateFilter !== 'all') {
      filtered = filtered.filter((crawl) => {
        const state = cityToState[crawl.city] || '';
        return state === stateFilter;
      });
    }

    setFilteredCrawls(filtered);
  };

  const cities = [...new Set(crawls.filter(c => !c.city?.toLowerCase().includes('test')).map((c) => c.city))];
  const filterPills = ['All', ...cities];
  const states = [...new Set(cities.map(c => cityToState[c]).filter(Boolean))].sort();

  return (
    <div className="min-h-screen pt-24" data-testid="crawls-page">
      <MetaTags
        title="Browse Crawls — Find Your Night"
        description="Explore bar crawls in 30+ cities across America. Pick your city, grab your crew, and get ready for the best night out."
        path="/crawls"
      />
      {/* Hero — Hot Pink */}
      <section className="relative py-16 pb-28 md:pb-16 px-4 overflow-hidden hero-diagonal-clip" style={{ backgroundColor: '#FF2D78' }} data-testid="crawls-hero">
        {/* Mobile-only photo bg + tint */}
        <div className="absolute inset-0 md:hidden">
          <img src="https://customer-assets.emergentagent.com/job_50c259f3-df0a-4de3-8458-52ce4eb888c4/artifacts/0adjl016_istockphoto-1368051592-612x612.jpg" alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(255,45,120,0.65)' }} />
        </div>
        {/* Mobile-only dark text scrim */}
        <div className="absolute inset-0 md:hidden pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(8,0,16,0.45) 0%, rgba(8,0,16,0.2) 50%, transparent 80%)' }} />
        <div className="max-w-[1400px] mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="flex items-center gap-3 font-mono uppercase text-xs tracking-[3px] text-white/80 md:text-[rgba(8,0,16,0.6)]">
              <span className="inline-block w-[20px] h-[2px] bg-white md:bg-[#FF2D78]" />
              Find Your Night
            </span>
            <h1 className="font-display text-[48px] md:text-[96px] uppercase mt-2 leading-none text-white md:text-[#080010]">
              Browse Crawls
            </h1>
            <p className="font-body text-lg mt-4 max-w-xl text-white/90 md:text-[rgba(8,0,16,0.7)]">
              Pick your city, grab your crew, and get ready for the best night out.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5px dark border — desktop only */}
      <div className="hidden md:block" style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* Filters — White */}
      <section className="px-4 py-8" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E5E5' }} data-testid="crawls-filters">
        <div className="max-w-[1400px] mx-auto">
          {/* City Dropdown + Search + State Filter */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative">
              <select
                value={activeFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'all') {
                    setSearchCity('');
                    setActiveFilter('all');
                  } else {
                    setSearchCity(val);
                    setActiveFilter(val);
                  }
                }}
                data-testid="crawls-city-filter"
                className="w-full md:w-auto pl-4 pr-10 py-3 bg-white border border-gray-300 font-subheading text-[13px] uppercase tracking-[1px] focus:outline-none focus:border-[#FF2D78] appearance-none cursor-pointer rounded"
                style={{ color: '#080010' }}
              >
                {filterPills.map((filter) => (
                  <option key={filter} value={filter === 'All' ? 'all' : filter}>
                    {filter === 'All' ? 'All Cities' : filter}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
            </div>

            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search city or neighborhood..."
                value={searchCity}
                onChange={(e) => {
                  setSearchCity(e.target.value);
                  setActiveFilter(e.target.value || 'all');
                }}
                data-testid="crawls-search-input"
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 font-body focus:outline-none focus:border-[#FF2D78] rounded"
                style={{ color: '#080010' }}
              />
            </div>

            <div className="relative">
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                data-testid="crawls-state-filter"
                className="w-full md:w-auto pl-4 pr-10 py-3 bg-white border border-gray-300 font-body focus:outline-none focus:border-[#FF2D78] appearance-none cursor-pointer rounded"
                style={{ color: '#080010' }}
              >
                <option value="all">All States</option>
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
            </div>
          </div>
        </div>
      </section>

      {/* 5px lime border */}
      <div style={{ height: '5px', backgroundColor: '#B8FF2E' }} />

      {/* Crawls Grid — Warm off-white */}
      <section className="px-4 py-16 pb-32" style={{ backgroundColor: '#F5F0EE' }} data-testid="crawls-grid">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#FF2D78] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredCrawls.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-body text-[#555] text-xl">No crawls found matching your criteria.</p>
              <p className="font-body text-[#999] mt-2">Try adjusting your filters or check back soon!</p>
            </div>
          ) : (
            <>
              <p className="font-mono text-xs tracking-wide mb-8" style={{ color: '#080010' }}>
                Showing {filteredCrawls.length} crawl{filteredCrawls.length !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCrawls.map((crawl, index) => (
                  <motion.div
                    key={crawl.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                  >
                    <CrawlCard crawl={crawl} />
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CrawlsPage;
